import { ElementType } from '../types';
import { FAB_COLORS } from '../components/canvas/amhs/rendering/fabColors';

/**
 * Fab floor & AMHS visual palette for 3D rendering.
 * Equipment uses cool silver-white (procedural AMHS kits); fab floor is glossy light gray.
 */
export const FACTORY_3D_PALETTE = {
  floor: {
    /** Fab layout slab — gray cleanroom floor (#94A3B8). */
    surface: '#94A3B8',
    /** Outer pad / scene surround — cleanroom white surround (#FFFFFF). */
    surround: '#FFFFFF',
    /** Secondary / depth variation. */
    slab: '#2A4868',
    slabDark: '#1E3450',
    lane: FAB_COLORS.floorLane,
    laneFill: '#E5B72C',
    laneEdge: '#F0CC5A',
    /** Grid mesh — subtle on blue floor. */
    grid: '#AEC2CE',
    gridOpacity: 0.35,
    bay: '#B8CCD8',
    wall: '#AEB7BF',
    pad: '#C0D4E0',
  },
  storage: {
    /** Stocker shell — cool white (matches CircleStocker kit). */
    body: FAB_COLORS.bodyWhite,
    accent: FAB_COLORS.graphite,
    plinth: FAB_COLORS.darkGraphite,
    shelf: FAB_COLORS.brushedMetal,
  },
  oht: {
    body: FAB_COLORS.bodyWhite,
    cab: FAB_COLORS.graphite,
    wheel: FAB_COLORS.metal,
    stripe: FAB_COLORS.cyan,
    glow: FAB_COLORS.cyanSoft,
  },
  rail: {
    track: FAB_COLORS.railOuter,
    beam: FAB_COLORS.railOuter,
    bracket: FAB_COLORS.railDark,
    glow: FAB_COLORS.cyan,
  },
  /** STB / standalone load port — silver-gray housing. */
  stb: {
    body: FAB_COLORS.loadPortBody,
    panel: FAB_COLORS.loadPortRecess,
    base: FAB_COLORS.brushedMetal,
  },
  ohb: {
    body: FAB_COLORS.bodyLight,
    panel: FAB_COLORS.graphite,
    base: FAB_COLORS.brushedMetal,
  },
  lifter: {
    body: FAB_COLORS.bodyWhite,
    frame: FAB_COLORS.graphite,
    platform: FAB_COLORS.brushedMetal,
    door: FAB_COLORS.loadPortRecess,
  },
  annotation: {
    rectangle: '#B8BDC1',
    circle: '#C8C9C6',
    text: '#475264',
  },
  /** Process equipment — cool silver-white (procedural kit). */
  fabEquipment: {
    body: FAB_COLORS.bodyWhite,
    accent: FAB_COLORS.graphite,
    panel: FAB_COLORS.bodyLight,
  },
  /** Support / soft zones (gowning, utility). */
  support: {
    body: '#B8BDC1',
    floor: '#E5E7E8',
    accent: '#405A78',
  },
  /** Single technology accent — status / indicator use only. */
  accent: {
    teal: FAB_COLORS.cyan,
    aqua: FAB_COLORS.cyanSoft,
    warmStatus: FAB_COLORS.amber,
  },
  /** Warm champagne LED illumination. */
  led: {
    champagne: '#F3C98B',
    warmWhite: '#FFF4DF',
  },
  /**
   * Scene-wide ambient light colors — roughly 70% cool neutral, 30% warm
   * champagne. Shared by every light in the 3D rigs so the whole fab reads as
   * one bright studio environment.
   */
  lighting: {
    /** Flat ambient fill — bright cleanroom without washing out equipment. */
    ambient: '#E8EDF1',
    /** Hemisphere sky / ground bounce. */
    skyTop: '#E4EBEF',
    skyGround: '#C8D2D8',
    /** Main key light — neutral cool, not pure white. */
    key: '#F0F4F7',
    /** Cool side fill. */
    fill: '#D8E2E8',
    /** Subtle warm accent rim. */
    warm: '#ECE6DE',
  },
} as const;

export type Factory3DStyle = {
  color: string;
  accent?: string;
  emissive?: string;
  emissiveIntensity?: number;
  metalness: number;
  roughness: number;
  opacity?: number;
};

const TYPE_STYLES: Record<ElementType, Factory3DStyle> = {
  stocker: {
    color: FACTORY_3D_PALETTE.storage.body,
    accent: FACTORY_3D_PALETTE.storage.accent,
    metalness: 0.18,
    roughness: 0.54,
  },
  'oht-vehicle': {
    color: FACTORY_3D_PALETTE.oht.body,
    accent: FACTORY_3D_PALETTE.oht.cab,
    emissive: FACTORY_3D_PALETTE.accent.teal,
    emissiveIntensity: 0.16,
    metalness: 0.34,
    roughness: 0.42,
  },
  'overhead-rail': {
    color: FACTORY_3D_PALETTE.rail.beam,
    accent: FACTORY_3D_PALETTE.rail.bracket,
    metalness: 0.72,
    roughness: 0.36,
  },
  'overhead-curve': {
    color: FACTORY_3D_PALETTE.rail.beam,
    accent: FACTORY_3D_PALETTE.rail.bracket,
    metalness: 0.72,
    roughness: 0.36,
  },
  'cross-rail': {
    color: FACTORY_3D_PALETTE.rail.beam,
    accent: FACTORY_3D_PALETTE.rail.bracket,
    metalness: 0.72,
    roughness: 0.36,
  },
  stb: {
    color: FACTORY_3D_PALETTE.stb.body,
    accent: FACTORY_3D_PALETTE.stb.panel,
    emissive: FACTORY_3D_PALETTE.accent.teal,
    emissiveIntensity: 0.1,
    metalness: 0.36,
    roughness: 0.42,
  },
  ohb: {
    color: FACTORY_3D_PALETTE.ohb.body,
    accent: FACTORY_3D_PALETTE.ohb.panel,
    emissive: FACTORY_3D_PALETTE.accent.teal,
    emissiveIntensity: 0.1,
    metalness: 0.36,
    roughness: 0.42,
  },
  lifter: {
    color: FACTORY_3D_PALETTE.lifter.body,
    accent: FACTORY_3D_PALETTE.lifter.platform,
    emissive: FACTORY_3D_PALETTE.accent.teal,
    emissiveIntensity: 0.08,
    metalness: 0.4,
    roughness: 0.44,
  },
  'support-pillar': {
    color: '#B8BDC1',
    accent: '#A7B0B8',
    metalness: 0.82,
    roughness: 0.32,
  },
  'horizontal-beam': {
    color: '#B8BDC1',
    metalness: 0.82,
    roughness: 0.32,
  },
  'suspension-rod': {
    color: '#A7B0B8',
    metalness: 0.78,
    roughness: 0.36,
    opacity: 0.92,
  },
  rectangle: {
    color: FACTORY_3D_PALETTE.annotation.rectangle,
    metalness: 0.1,
    roughness: 0.85,
    opacity: 0.35,
  },
  circle: {
    color: FACTORY_3D_PALETTE.annotation.circle,
    metalness: 0.1,
    roughness: 0.85,
    opacity: 0.35,
  },
  text: {
    color: FACTORY_3D_PALETTE.annotation.text,
    metalness: 0,
    roughness: 1,
  },
};

export function getFactory3DStyle(type: ElementType): Factory3DStyle {
  return TYPE_STYLES[type] ?? {
    color: FACTORY_3D_PALETTE.fabEquipment.body,
    metalness: 0.4,
    roughness: 0.5,
  };
}

/** DXF / floor-plan layer colors for 3D line rendering. */
export const FLOOR_LAYER_COLORS: Record<string, { color: string; opacity: number }> = {
  walls: { color: FACTORY_3D_PALETTE.floor.wall, opacity: 0.95 },
  grid: {
    color: FACTORY_3D_PALETTE.floor.grid,
    opacity: FACTORY_3D_PALETTE.floor.gridOpacity,
  },
  bays: { color: FACTORY_3D_PALETTE.floor.bay, opacity: 0.5 },
  aisles: { color: FACTORY_3D_PALETTE.floor.lane, opacity: 0.85 },
  'lane-fill': { color: FACTORY_3D_PALETTE.floor.laneFill, opacity: 0.18 },
  'storage-zones': { color: FACTORY_3D_PALETTE.storage.plinth, opacity: 0.14 },
  'equipment-pads': { color: FACTORY_3D_PALETTE.floor.pad, opacity: 0.55 },
};

export function getFloorLayerStyle(layer: string): { color: string; opacity: number } {
  return FLOOR_LAYER_COLORS[layer] ?? { color: '#3b82f6', opacity: 0.55 };
}

/** Stable hue for imported DXF layer names (328+ layers). */
export function colorFromLayerName(layer: string): string {
  let hash = 0;
  for (let i = 0; i < layer.length; i++) {
    hash = (hash * 31 + layer.charCodeAt(i)) | 0;
  }
  const hue = Math.abs(hash) % 360;
  // Low saturation keeps imported layers distinguishable without breaking the
  // neutral slate palette.
  return `hsl(${hue}, 20%, 62%)`;
}

/** Default stroke for merged imported DXF floor geometry in 3D. */
export const IMPORTED_DXF_FLOOR_COLOR = '#334155';
/** Main DXF stroke opacity in 3D — keep near-opaque so ACI colors stay vivid. */
export const IMPORTED_DXF_FLOOR_OPACITY = 1;
/** Soft underlay pass to thicken thin WebGL lines. */
export const IMPORTED_DXF_FLOOR_UNDERLAY = '#94a3b8';
export const IMPORTED_DXF_FLOOR_UNDERLAY_OPACITY = 0.55;

/** Fab tile slab opacity when a DXF underlay is visible beneath it (3D, 0x6e / 255 = ~0.4314). */
export const FAB_CLEANROOM_FLOOR_OVER_DXF_OPACITY = 0.43;
/** Grid lines on the fab slab when DXF is highlighted underneath. */
export const FAB_CLEANROOM_GRID_OVER_DXF_OPACITY = 0.16;

/** Unused underlay fallback — DXF rasters sit on the shared tiled floor. */
export const SIMULATION_RASTER_FLOOR_COLOR = FACTORY_3D_PALETTE.floor.surface;
export const SIMULATION_RASTER_FLOOR_COLOR_DARK = FACTORY_3D_PALETTE.floor.slabDark;

/**
 * Extruded floor-plan walls — painted cleanroom panel, cool neutral so it sits
 * in the same family as the slate floor instead of fighting it.
 *
 * Kept fully opaque on purpose: a partition that lets the plan read through it
 * looks like glass, and a translucent wall also has to drop out of the depth
 * buffer, which lets every line behind it draw on top.
 */
export const FACTORY_3D_WALL = {
  color: '#B9C2CC',
  /** Paint, not bare metal — high metalness is what washed this out before. */
  metalness: 0.06,
  roughness: 0.78,
} as const;
